/**
 * The "Update available" toast: a small non-blocking banner in the bottom
 * corner, shown when a new service worker is waiting to activate. "Reload"
 * accepts the update; "Not now" dismisses it for the session (no
 * persistence - the next page load re-detects the waiting worker and shows
 * the prompt again).
 *
 * The markup lives in index.html; this class wires it up. It never moves
 * focus and never covers interactive content.
 */
class UpdatePrompt {

    private readonly prompt: HTMLElement;
    private onAccept: (() => void) | null = null;
    private dismissed = false;

    constructor() {
        this.prompt = document.getElementById('update-prompt')!;
        document.getElementById('update-reload')!.addEventListener('click', () => this.accept());
        document.getElementById('update-dismiss')!.addEventListener('click', () => this.dismiss());
    }

    /** Show the prompt; onAccept runs when the user picks "Reload". */
    show(onAccept: () => void) {
        if (this.dismissed) {
            return;
        }
        this.onAccept = onAccept;
        this.prompt.hidden = false;
        // add the class on the next frame so the slide-up transition runs
        requestAnimationFrame(() => this.prompt.classList.add('open'));
    }

    private accept() {
        const onAccept = this.onAccept;
        this.hide();
        onAccept?.();
    }

    private dismiss() {
        this.dismissed = true;
        this.hide();
    }

    private hide() {
        this.onAccept = null;
        this.prompt.classList.remove('open');
        const done = () => {
            if (!this.prompt.classList.contains('open')) {
                this.prompt.hidden = true;
            }
        };
        this.prompt.addEventListener('transitionend', done, {once: true});
        // fallback in case the transition is interrupted and never ends
        setTimeout(done, 300);
    }
}

export {UpdatePrompt};
